import styled from 'styled-components'
import { layout, LayoutProps, size, SizeProps, SpaceProps, space } from 'styled-system'
import { AdjustColorTint, ThemeColor } from 'utils/colors'

import Text from '../Text'

type SizeVariant = 'sm' | 'md' | 'lg'

type Props = LayoutProps &
  SpaceProps &
  SizeProps & {
    name?: string
    label?: string
    value?: string
    options?: { label: string; value: string }[]
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
    size?: SizeVariant
    color?: ThemeColor
  }

function ToggleSwitch({ name, label, value, options, onChange, size = 'md', color = 'blue1', ...cssProps }: Props) {
  return (
    <Container {...cssProps}>
      {label && <Text pb="0.2rem">{label}</Text>}
      <Wrapper color={color}>
        {options?.map((option, index) => {
          const isSelected = option.value === value

          return (
            <ToggleItem
              key={index}
              color={color}
              size={size}
              onClick={() => {
                onChange?.({
                  target: {
                    name: name ?? '',
                    value: option.value,
                  },
                } as React.ChangeEvent<HTMLInputElement>)
              }}
              isSelected={isSelected}
            >
              <Text variant="caption1" color="blue1">
                {option.label}
              </Text>
              <input key={index} type="radio" />
            </ToggleItem>
          )
        })}
      </Wrapper>
    </Container>
  )
}

const Container = styled.div<LayoutProps & SizeProps>`
  width: fit-content;
  ${layout}
  ${space}
  ${size}
`

const Wrapper = styled.div<{ color: ThemeColor }>`
  display: flex;
  gap: 0;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.colors[p.color]};
  border-radius: 4px;

  * {
    cursor: pointer;
    user-select: none;
  }
`

const ToggleItem = styled.div<{ isSelected: boolean; size?: SizeVariant; color: ThemeColor }>`
  display: flex;
  align-items: center;
  padding: ${(p) => {
    switch (p.size) {
      case 'sm':
        return '0.2rem 0.5rem'
      case 'md':
        return '0.7rem 1rem'
      case 'lg':
        return '1rem 1.25rem'
    }
  }};

  input {
    position: absolute;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: -1;
  }

  :not(:last-child) {
    border-right: 1px solid ${(p) => p.theme.colors[p.color]};
  }

  ${({ isSelected, color, theme }) =>
    isSelected
      ? `
    background-color: ${theme.colors[color]};
    * {
      color: ${theme.colors.white};
    }
  `
      : `
    background-color: ${theme.colors.white};
    * {
      color: ${theme.colors[color]};
    }
    
    &:hover {
      background-color: ${AdjustColorTint(theme.colors[color], 50)};

      * {
        color: ${theme.colors.white};
      }
    }
  `}
`

export default ToggleSwitch
